<template>
  <section class="privacy-wrapper">
    <div class="privacy-container">
      <h1 class="page-heading">{{appName}} Privacy Statement</h1>
      <div class="divider"></div>
      <h2 class="sub-heading">User Data Protection Disclosure</h2>

      <p class="text-desc">
        When you access our mobile application and utilize our services, we may collect and process your personal data.
        This statement elaborates on our standards for collecting, utilizing and disclosing your personal information during service use.
      </p>

      <p class="prompt-text">
        Please take your time to read this statement thoroughly to fully understand how we protect your personal rights and data security.
      </p>

      <div class="catalog-block">
        <h3 class="catalog-title">Table of Contents</h3>
        <ul class="catalog-list">
          <li>1. Collection & Application of Your Personal Data</li>
          <li>2. Sharing, Transfer and Public Disclosure of Personal Data</li>
          <li>3. Deployment of Cookies and Third-Party SDKs</li>
          <li>4. Security Safeguards for Personal Information</li>
          <li>5. Your Rights to Control Personal Data</li>
          <li>6. Data Handling Rules for Underage Users</li>
          <li>7. Storage Specifications of User Information</li>
          <li>8. Updates and Notification Mechanism of This Statement</li>
          <li>9. Contact Information for Inquiries</li>
        </ul>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">Preface</h3>
        <p class="body-text">
          {{appName}} attaches paramount importance to user data confidentiality. We maintain a complete information protection framework and strictly safeguard all collected user data, to deliver stable, secure and reliable services to every individual user.
        </p>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">1. Collection & Application of Personal Data</h3>
        <p class="body-text">
          Personal data refers to all electronically recorded information capable of identifying a specific individual or reflecting personal behaviors independently or in combination, including full name, date of birth, biometric identifiers, residential address, contact details, chat logs, account credentials, asset records, physical health data and transaction history.
        </p>
        <p class="body-text">
          Sensitive personal data refers to information that would trigger risks to personal and property safety, reputational harm or unfair discrimination upon leakage, illegal transmission or abuse, covering biometric data, bank account details, full chat content, asset status, location trails, medical records, transaction data and personal information of users under the age of 14.
        </p>

        <p class="emphasis-text">You hereby acknowledge and consent to the following provisions:</p>
        <ol class="order-list">
          <li>We develop diversified service modules to meet your personalized demands. Different functions will collect distinct categories and ranges of personal data, subject to the specific service features you choose to use.</li>
          <li>We continuously iterate product technologies to upgrade user experience. When rolling out new or optimized features, we may need to gather new categories of data or adjust existing data usage logic. We will notify you via statement revisions, in-app pop-ups or page prompts, and collect relevant data only after obtaining your explicit consent. Feel free to reach out to us with any questions or feedback.</li>
          <li>Data acquired via one functional module may be deployed across other services on our platform, including customized content push and security risk reminders. If we intend to utilize your data beyond the initially authorized scope, we will send separate notifications and obtain your prior approval.</li>
        </ol>

        <p class="warning-label">Special Notice</p>
        <p class="body-text">
          Our platform provides user matching and recommendation functions. The nickname, age, gender, zodiac sign, occupation, company, hometown, regular activity locations, personal signature, uploaded avatars, photos and public dynamic content you fill out may be visible and recommended to other platform users.
        </p>

        <h4 class="sub-chapter-title">Business Modules & Corresponding Collected Data</h4>
        <p class="body-text">
          Classified as a social interaction application per relevant industry standards, {{appName}}’s core capability is online instant messaging supporting text, images, voice and video. The data collected by each function are listed below:
        </p>
        <ol class="function-list">
          <li><span class="func-name">Account Registration</span>: To complete registration, you are required to submit email address, nickname, gender, country, birth date and avatar. We also collect device metadata such as device model, OS version, unique device identifiers, network parameters, installed application inventory and sensor information to guarantee stable software operation.</li>
          <li><span class="func-name">Instant Chat</span>: Basic chat functions require no extra personal data. We store your account information to maintain service stability and account safety. Sending images, voice notes or initiating video calls will trigger requests for album and microphone permissions; denying these permissions will not disrupt core chat experience.</li>
          <li><span class="func-name">Video Call</span>: Camera and microphone permissions are mandatory for this feature. Normal video communication cannot be realized without your authorization.</li>
          <li><span class="func-name">Moments Browsing</span>: No personal data or additional system permissions are needed to view public moments content.</li>
          <li><span class="func-name">User Homepage Visit</span>: Viewing others’ uploaded photos and videos requires no extra permissions; initiating video calls from user homepages requires camera and microphone access.</li>
          <li><span class="func-name">Profile Editing</span>: Album permission will be requested when replacing your avatar, and refusal will not block core application functions.</li>
          <li><span class="func-name">Payment Channel</span>: Payment services are provided by third-party payment partners, and we cannot access your payment-related data. Disabling relevant permissions only restricts payment features, leaving other core services unaffected.</li>
          <li><span class="func-name">Message Notification</span>: We apply for auto-start permissions to deliver timely message alerts, which you can disable anytime within your mobile system settings.</li>
        </ol>

        <p class="body-text">
          Camera, album and microphone permissions remain disabled by default. Independent authorization pop-ups will appear when you access relevant features, and you may toggle these permissions freely in device settings. Disabling permissions revokes your data collection authorization, and we will cease corresponding data gathering; this action does not invalidate data previously collected with your consent.
        </p>
        <p class="body-text">
          We may deliver service alerts, security verification codes and user research questionnaires to your reserved email address. If you publish third-party personal information on our platform, you must obtain legal authorization beforehand.
        </p>
        <p class="body-text">
          We retain and utilize your submitted data during your service lifecycle. After account cancellation or discontinuation of specific functions, we will stop data usage and perform anonymization or permanent deletion in compliance with legal regulations.
        </p>

        <h4 class="sub-chapter-title">Standards for Utilizing Collected Data</h4>
        <p class="body-text">
          All collected data will only be applied for purposes stated within this statement. Any cross-scenario data usage will require your separate consent. When acquiring data from third-party suppliers, we demand written proof that the third party has obtained user consent before data collection, and enforce identical high-level security protection on externally sourced data as our native user information.
        </p>

        <p class="emphasis-text">Exemptions from Consent Requirements</p>
        <ul class="simple-list">
          <li>Fulfillment of statutory legal obligations</li>
          <li>Matters concerning national security and national defense</li>
          <li>Matters related to public safety and public health welfare</li>
          <li>Criminal investigation, litigation, trial and judgment enforcement procedures</li>
          <li>Scenarios where consent cannot be reasonably obtained to protect personal legitimate rights</li>
          <li>Information actively disclosed to the public by yourself</li>
          <li>Data extracted from legally public information channels</li>
        </ul>
        <p class="body-text">
          We conduct desensitization, statistical analysis and algorithm training on anonymized user data to optimize product iterations. Processed anonymous data cannot trace back to specific individuals and requires no additional user notification or consent. If we terminate platform operations, we will suspend all data collection activities, issue service termination announcements, and fully anonymize or erase all stored personal records.
        </p>

        <h4 class="sub-chapter-title">Content Published Voluntarily by You</h4>
        <p class="body-text">
          You may share text, images and multimedia content across our platform, which may carry personal or sensitive information. Please exercise caution before publishing private data. You can delete your published content at any time; however, such content may still be stored by other users or independent third parties beyond our management scope.
        </p>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">2. Sharing, Transfer & Public Disclosure of Personal Data</h3>
        <h4 class="sub-chapter-title">Data Sharing</h4>
        <p class="body-text">We will not share your personal data with external enterprises, organizations or individuals except the following scenarios:</p>
        <ul class="simple-list">
          <li>Data sharing approved by your explicit written consent</li>
          <li>Mandatory data disclosure required by laws, administrative or judicial authorities</li>
          <li>Limited necessary data sharing with affiliated brands to deliver cross-account joint services; renewed consent will be requested if sensitive data or changed usage purposes are involved</li>
          <li>Data provision to authorized service partners, who may only access information necessary to complete entrusted tasks</li>
        </ul>

        <h4 class="sub-chapter-title">Data Transfer</h4>
        <p class="body-text">We will not transfer your personal data to any external entity unless:</p>
        <ul class="simple-list">
          <li>You have provided explicit consent for data transfer</li>
          <li>Mergers, acquisitions or bankruptcy liquidation occur; new data holders must abide by this statement, otherwise renewed user authorization shall be acquired</li>
        </ul>

        <h4 class="sub-chapter-title">Public Disclosure</h4>
        <ul class="simple-list">
          <li>Disclosure triggered by your active operation and explicit consent</li>
          <li>Disclosure enforced by laws and platform rules to safeguard public interests when you violate regulatory agreements</li>
        </ul>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">3. Deployment of Cookies and Third-Party SDKs</h3>
        <h4 class="sub-chapter-title">Cookies Technology</h4>
        <p class="body-text">
          We store lightweight data files named Cookies on your device to maintain stable service operation, streamline browsing experience and push tailored content. You may clear Cookies via browser settings, though some feature sets dependent on Cookies may become inaccessible afterward.
        </p>

        <h4 class="sub-chapter-title">Third-Party SDK Integration</h4>
        <p class="body-text">
          Partial core functions rely on third-party SDK services provided by cooperative suppliers, who independently apply for system permissions and collect corresponding data. Integrated SDK list includes Google Firebase Push SDK, Third-party Payment SDK, NetEase Yunxin SDK, Shengwang SDK, AppsFlyer SDK, Facebook SDK, Huawei Push SDK. Refusing relevant permissions only disables corresponding third-party functions and will not interfere with other core services of {{appName}}.
        </p>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">4. Security Safeguards for Personal Information</h3>
        <ol class="order-list">
          <li>We adopt industrial-standard encryption, desensitization and access control mechanisms to block unauthorized access, leakage, damage and loss of personal data, alongside regular data protection training for internal staff.</li>
          <li>A full lifecycle data security management system is established. In the event of data leakage incidents, emergency response plans will be activated immediately, and users will be notified via official announcements or in-app pushes.</li>
          <li>We only collect minimal necessary data and retain user information solely for the service lifecycle required by stated purposes, unless legal provisions mandate extended retention periods.</li>
          <li>The internet cannot guarantee absolute information security. Avoid transmitting sensitive personal data through unofficial communication channels. Immediately contact customer service if you discover account or password leakage.</li>
        </ol>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">5. Your Rights to Control Personal Data</h3>
        <h4 class="sub-chapter-title">Data Inquiry</h4>
        <p class="body-text">
          You hold the right to access your personal data within legal boundaries. Access and modification entries: <strong>[Mine - Top Right Corner - Settings]</strong>. Tap your avatar in the personal center to view and edit nickname, avatar, birth date and self-introduction.
        </p>

        <h4 class="sub-chapter-title">Data Correction</h4>
        <p class="body-text">You may submit correction requests through the above entry if you detect inaccurate personal records stored by us.</p>

        <h4 class="sub-chapter-title">Data Deletion</h4>
        <p class="body-text">You can manually erase partial personal data, or submit deletion requests under the following circumstances:</p>
        <ul class="simple-list">
          <li>Our data processing activities violate applicable laws</li>
          <li>Data is collected and utilized without your explicit consent</li>
          <li>We materially breach the agreements stipulated in this statement</li>
          <li>You cease service usage or submit an account cancellation application</li>
          <li>We permanently terminate service provision for your account</li>
        </ul>

        <h4 class="sub-chapter-title">Adjust Authorization Scope</h4>
        <p class="body-text">You may independently enable or revoke data collection permissions, or reach out to customer service for assistance with authorization adjustments.</p>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">6. Data Handling Rules for Underage Users</h3>
        <p class="hard-warning">This application prohibits registration and usage by users under 18 years of age.</p>
        <p class="body-text">
          Our services are not oriented to minors under 18. We will not actively collect or trade minor personal information. If we unintentionally obtain data from underage users, such records will be erased instantly. Should we adjust relevant policies in the future, verifiable guardian consent will be acquired prior to collecting any minor user data.
        </p>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">7. Storage Specifications of User Information</h3>
        <p class="body-text">
          Our service servers are deployed across multiple global jurisdictions. Your personal data may be stored and processed on servers outside your local region. You consent to cross-border transmission and storage of your data for all purposes outlined within this statement.
        </p>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">8. Updates and Notification Mechanism</h3>
        <p class="body-text">
          We reserve the right to revise this privacy statement at our discretion. You may review the latest version via the official website link at any time. Material content revisions will be announced via website notifications or in-app alerts. Your continued use of services after statement updates constitutes full acceptance of revised terms. Discontinue service access if you disagree with updated clauses.
        </p>
      </div>

      <div class="chapter-block">
        <h3 class="chapter-title">9. Contact Information</h3>
        <p class="body-text">
          For inquiries, suggestions or disputes related to this privacy statement and data protection issues, please send emails to:
          <span class="mail-text">{{appName}}@{{appName}}.site</span>
        </p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  data() {
    return {}
  },
  created() {
    // 预留业务生命周期
  }
}
</script>

<style scoped>
/* 简约高级低饱和极简风格 高留白 商务轻奢 */
.privacy-wrapper {
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  background-color: #f7f7f9;
  padding: 40px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif;
}
.privacy-container {
  max-width: 720px;
  margin: 0 auto;
  background: #ffffff;
  padding: 48px 40px;
  border-radius: 4px;
}
.page-heading {
  font-size: 28px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: center;
  margin: 0 0 12px;
  letter-spacing: 0.5px;
}
.divider {
  width: 60px;
  height: 1px;
  background-color: #d0d0d8;
  margin: 0 auto 24px;
}
.sub-heading {
  font-size: 18px;
  font-weight: 400;
  color: #44444f;
  text-align: center;
  margin: 0 0 32px;
}
.text-desc {
  font-size: 15px;
  line-height: 1.8;
  color: #555566;
  margin: 0 0 16px;
  text-align: justify;
}
.prompt-text {
  font-size: 15px;
  line-height: 1.8;
  color: #334155;
  padding: 12px 16px;
  background-color: #f4f5f8;
  border-left: 2px solid #64748b;
  margin: 0 0 36px;
}
.catalog-block {
  margin-bottom: 40px;
}
.catalog-title {
  font-size: 16px;
  font-weight: 500;
  color: #22222c;
  margin: 0 0 12px;
}
.catalog-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.catalog-list li {
  font-size: 14px;
  color: #555566;
  line-height: 1.9;
  margin: 6px 0;
}
.chapter-block {
  margin-bottom: 42px;
}
.chapter-title {
  font-size: 17px;
  font-weight: 500;
  color: #22222c;
  margin: 0 0 16px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}
.sub-chapter-title {
  font-size: 16px;
  font-weight: 500;
  color: #333344;
  margin: 24px 0 10px;
}
.body-text {
  font-size: 15px;
  line-height: 1.8;
  color: #555566;
  margin: 0 0 14px;
  text-align: justify;
}
.emphasis-text {
  font-size: 15px;
  font-weight: 500;
  color: #992e2e;
  margin: 12px 0 8px;
}
.warning-label {
  font-size: 15px;
  font-weight: 500;
  color: #992e2e;
  margin: 12px 0 4px;
}
.hard-warning {
  font-size: 16px;
  font-weight: 500;
  color: #c41e3a;
  margin: 10px 0;
}
.order-list, .function-list {
  padding-left: 20px;
  margin: 0 0 16px;
}
.order-list li, .function-list li {
  font-size: 15px;
  line-height: 1.8;
  color: #555566;
  margin: 8px 0;
}
.func-name {
  font-weight: 500;
  color: #22222c;
}
.simple-list {
  padding-left: 20px;
  margin: 0 0 16px;
}
.simple-list li {
  font-size: 15px;
  line-height: 1.8;
  color: #555566;
  margin: 6px 0;
}
.mail-text {
  color: #294b80;
  font-weight: 500;
}
</style>